import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/admin/Ui";
import { CurrencyDollar, ChartBar, Power } from "@phosphor-icons/react";

export default function Budget() {
  const [data, setData] = useState(null);
  const [caps, setCaps] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/budget");
    setData(data);
    setCaps(data.caps);
  };
  useEffect(() => { load(); }, []);

  const saveCaps = async () => {
    setBusy(true);
    try {
      await api.patch("/budget/caps", {
        daily_usd_cap: Number(caps.daily_usd_cap),
        monthly_usd_cap: Number(caps.monthly_usd_cap),
        daily_run_cap: Number(caps.daily_run_cap),
        hard_stop: caps.hard_stop,
        kill_switch: caps.kill_switch,
      });
      toast.success("Caps saved");
      await load();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const killSwitch = async (next) => {
    setBusy(true);
    try {
      await api.patch("/budget/caps", { kill_switch: next });
      toast[next ? "error" : "success"](next ? "Global AI kill-switch ENGAGED" : "Kill switch disarmed");
      await load();
    } finally { setBusy(false); }
  };

  if (!data || !caps) return <div className="p-8 text-navy/60">Loading…</div>;

  const today = data.today, month = data.month;
  const todayPct = Math.min(100, (today.spend / Math.max(0.0001, today.cap)) * 100);
  const monthPct = Math.min(100, (month.spend / Math.max(0.0001, month.cap)) * 100);
  const runsPct = Math.min(100, (today.runs / Math.max(1, today.run_cap)) * 100);

  const maxBurn = Math.max(0.0001, ...data.burn_14d.map((b) => b.cost_usd));
  const maxAgent = Math.max(0.0001, ...data.attribution_30d.map((a) => a.cost_usd));

  return (
    <div data-testid="admin-budget-page">
      <PageHeader title="AI Budget" subtitle="LLM cost governance" />
      <div className="p-8 space-y-6">
        {/* Kill switch */}
        <div className={`p-5 border ${caps.kill_switch ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"} flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <Power size={26} weight="duotone" className={caps.kill_switch ? "text-red-600" : "text-emerald-600"} />
            <div>
              <div className={`font-display font-bold ${caps.kill_switch ? "text-red-700" : "text-emerald-700"}`}>
                {caps.kill_switch ? "Global AI Kill Switch — ENGAGED" : "Global AI Kill Switch — armed & ready"}
              </div>
              <div className="text-xs text-navy/60 mt-0.5">Emergency brake: stops every LLM call (drafting + generation).</div>
            </div>
          </div>
          <button
            onClick={() => killSwitch(!caps.kill_switch)}
            disabled={busy}
            data-testid="budget-killswitch-button"
            className={`px-4 py-2 text-sm font-semibold uppercase tracking-widest ${caps.kill_switch ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-red-600 text-white hover:bg-red-700"}`}
          >
            {caps.kill_switch ? "Disarm" : "Stop all AI"}
          </button>
        </div>

        {/* Spend cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card label="Today" value={`$${today.spend.toFixed(2)}`} sub={`/ $${today.cap.toFixed(2)}`} pct={todayPct} testId="budget-today" />
          <Card label="This month" value={`$${month.spend.toFixed(2)}`} sub={`/ $${month.cap.toFixed(2)}`} pct={monthPct} testId="budget-month" />
          <Card label="Runs today" value={today.runs} sub={`/ ${today.run_cap}`} pct={runsPct} testId="budget-runs" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Workspace caps */}
          <div className="lg:col-span-5 bg-white border border-black/10 p-6">
            <div className="flex items-center gap-2 mb-4">
              <CurrencyDollar size={16} weight="duotone" className="text-ember" />
              <div className="overline">Workspace caps · spending limits</div>
            </div>
            <div className="space-y-4 max-w-md">
              <Field label="Daily USD cap">
                <input data-testid="budget-daily-cap" type="number" step="0.1" value={caps.daily_usd_cap}
                  onChange={(e) => setCaps({ ...caps, daily_usd_cap: e.target.value })} className={input} />
              </Field>
              <Field label="Monthly USD cap">
                <input data-testid="budget-monthly-cap" type="number" step="0.1" value={caps.monthly_usd_cap}
                  onChange={(e) => setCaps({ ...caps, monthly_usd_cap: e.target.value })} className={input} />
              </Field>
              <Field label="Daily AI run count cap">
                <input data-testid="budget-run-cap" type="number" value={caps.daily_run_cap}
                  onChange={(e) => setCaps({ ...caps, daily_run_cap: e.target.value })} className={input} />
              </Field>
              <label className="flex items-center gap-2 text-sm text-navy/70">
                <input data-testid="budget-hard-stop" type="checkbox" checked={caps.hard_stop}
                  onChange={(e) => setCaps({ ...caps, hard_stop: e.target.checked })} />
                Hard stop when caps hit (otherwise soft warn only)
              </label>
              <button onClick={saveCaps} disabled={busy} data-testid="budget-save-button" className="btn-primary w-full justify-center mt-2">
                {busy ? "Saving…" : "Save caps"}
              </button>
            </div>
          </div>

          {/* Attribution */}
          <div className="lg:col-span-7 bg-white border border-black/10 p-6">
            <div className="flex items-center gap-2 mb-4">
              <ChartBar size={16} weight="duotone" className="text-ember" />
              <div className="overline">30-day spend by agent · cost attribution</div>
            </div>
            {data.attribution_30d.length === 0 ? (
              <p className="text-sm text-navy/50">No spend recorded yet. Run an agent to start tracking.</p>
            ) : (
              <ul className="space-y-3" data-testid="budget-attribution-list">
                {data.attribution_30d.map((a) => (
                  <li key={a.agent} data-testid={`budget-attribution-${a.agent}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-mono text-navy">{a.agent}</span>
                      <span className="text-navy/70">${a.cost_usd.toFixed(4)} · {a.runs} runs</span>
                    </div>
                    <div className="h-1 bg-navy/10 mt-1">
                      <div className="h-1 bg-ember" style={{ width: `${(a.cost_usd / maxAgent) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Burn trend */}
        <div className="bg-white border border-black/10 p-6">
          <div className="overline mb-4">Daily spend · 14d burn trend</div>
          {data.burn_14d.length === 0 ? (
            <p className="text-sm text-navy/50">No spend recorded yet.</p>
          ) : (
            <div className="flex items-end gap-2 h-40" data-testid="budget-burn-chart">
              {data.burn_14d.map((b) => (
                <div key={b.day} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="w-full bg-navy/80 hover:bg-ember transition-colors" style={{ height: `${(b.cost_usd / maxBurn) * 100}%` }} title={`$${b.cost_usd}`} />
                  <span className="text-[9px] text-navy/40">{b.day.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const input = "w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy";
const Field = ({ label, children }) => (
  <div><label className="overline mb-2 block">{label}</label>{children}</div>
);

const Card = ({ label, value, sub, pct, testId }) => (
  <div className="bg-white border border-black/10 p-5" data-testid={testId}>
    <div className="overline">{label}</div>
    <div className="mt-2 flex items-end gap-2">
      <div className="ship-number text-4xl font-black text-navy leading-none">{value}</div>
      <div className="text-sm text-navy/50 pb-1">{sub}</div>
    </div>
    <div className="h-1 mt-3 bg-navy/10">
      <div className={`h-1 ${pct > 90 ? "bg-red-500" : pct > 70 ? "bg-amber-500" : "bg-ember"}`} style={{ width: `${pct}%` }} />
    </div>
    <div className="text-[10px] uppercase tracking-widest text-navy/40 mt-2">{pct.toFixed(0)}% used</div>
  </div>
);
